import {
  Button,
  cn,
  Dialog,
  DialogProps,
  TimeUnit,
  useLocalStorage,
} from '@sandtech-hub-monorepo/ui-kit';
import { InfoIcon } from 'lucide-react';
import { HeaderLink } from './HeaderLink';
import { GetProgramEnrollmentPaymentPlansQuery } from '@sandtech-hub-monorepo/ehub-client';
import { ExternalApplicationLinks } from '../../../utils';
import { ReactComponent as BulbIcon } from '../../assets/Icons/bulb.svg';
import { useMemo } from 'react';

interface Props extends Omit<DialogProps, 'onOpenChange'> {
  isPathway: boolean;
  isSet: boolean;
  paymentPlan?:
    | NonNullable<
        GetProgramEnrollmentPaymentPlansQuery['viewer']['paymentPlans']
      >[number]
    | null;
  sdk: boolean;
  onClose?: () => void;
}

export const PaywallDialog = ({
  isPathway,
  isSet,
  sdk,
  paymentPlan,
  open,
  onClose,
  ...props
}: Props) => {
  // For SET, throttle the paywall appearance to once in the past 24h.
  const [lastPaywallAppearance, setLastPaywallAppearance] = useLocalStorage(
    'lastPaywallAppearance',
    0
  );
  const paywallOpen = useMemo(() => {
    if (!isSet || !open) {
      return open;
    }
    // Whenever the case is that our paywall is "open", we check for the throttle.
    const paywallOpenedLast24h =
      new Date(lastPaywallAppearance).valueOf() > Date.now() - TimeUnit.DAY;
    if (paywallOpenedLast24h) {
      return false;
    }
    // At this point we decided the paywall will be open so we mark the timestamp.
    setLastPaywallAppearance(Date.now());
    return true;
  }, [open, isSet]);

  return (
    <Dialog
      {...props}
      open={paywallOpen}
      onOpenChange={isSet ? onClose : undefined}
    >
      <div className="mx-auto">
        <div className="px-0 sm:px-16">
          <div className="flex flex-col items-center text-center gap-2">
            <div
              className={`p-6 bg-destructive/20 rounded-full text-destructive mx-auto mb-2`}
            >
              <InfoIcon height="48" width="48" />
            </div>
            <h2 className={`font-semibold sm:text-2xl text-xl`}>
              {paymentPlan ? 'Payment required' : 'Access locked'}
            </h2>
            <div className="text-destructive bg-destructive/20 p-2.5 rounded-sm my-4">
              {paymentPlan
                ? `Your ${paymentPlan?.definition?.name} payment plan is not up to date. Please make a payment now to access your learning content.`
                : 'You need to activate your ALX Access payment plan to unlock your learning content.'}
            </div>
          </div>
          <div className="flex gap-6 justify-center my-8">
            <HeaderLink
              href={paymentPlan?.viewUrl || ExternalApplicationLinks.payments}
              target="_blank"
              sdk={sdk}
            >
              <Button>
                {paymentPlan ? 'Make a payment now' : 'Activate payment plan'}
              </Button>
            </HeaderLink>
          </div>
          {!isPathway && (
            <div className="bg-primary/10 p-2.5 rounded-md flex gap-3 items-center">
              <div className="p-3 rounded-full border border-primary/20 bg-background text-primary">
                <BulbIcon />
              </div>
              <div className="text-secondary">
                <b>TIP:</b> Make learning hassle-free—switch to a one-time $49
                payment for 12 months of access and never worry about monthly
                payments again!
              </div>
            </div>
          )}

          <div className="text-center text-sm my-12">
            {paymentPlan
              ? 'If you need support with your payment or if you think you are seeing this in error, '
              : 'Need help? '}
            <HeaderLink
              href={ExternalApplicationLinks.support!}
              target="_blank"
              className="font-semibold text-primary"
              sdk={sdk}
            >
              {paymentPlan ? 'c' : 'C'}lick here
            </HeaderLink>
          </div>
        </div>
        <div
          className={cn(
            'flex gap-6 justify-center my-8',
            isSet ? 'bg-white' : ''
          )}
        >
          <HeaderLink
            href={
              isSet
                ? ExternalApplicationLinks.ehub.replace('ehub', 'set')
                : ExternalApplicationLinks.ehub
            }
            target="_self"
            sdk={sdk}
          >
            <Button variant="outline">Return to eHub</Button>
          </HeaderLink>
        </div>
      </div>
    </Dialog>
  );
};
